// The ALERTS card: the ALERTS section of the hub SETTINGS panel and of the
// ☸ OPTIONS menu of the game (player-reach.md §3.5, R1301; the BUZZ column
// of mockup round 077, card 11, R1612; the look of card 16 of round 081,
// R1731).
//
// What it shows: one row per buzz event (`BUZZ_EVENTS`) with a PUSH and a
// BUZZ switch side by side, the pattern drawn to its length and ▶ TRY,
// then the quiet hours and the switch "siege at night". A PUSH switch is
// the switch of the row's push class, so the PUSH column holds the classes
// in the order of `PUSH_CLASSES`; the hit on a fleet has no push class and
// reads —. Every push class is off by default and every BUZZ switch is on
// (R1613), and the quiet hours are 23:00 to 07:00 (§3.2). The time zone is
// the browser's, and the card names it beside the hours.
//
// The form of the device decides the BUZZ column (`buzzForm`, R1615): a
// telephone with a motor shows BUZZ, the pattern and TRY; a telephone with
// none (an iPhone) reads — in BUZZ, the lede says why, and TRY is gone; a
// desktop has no motor, so the card shows the PUSH column alone and the row
// of the hit, which has no push, is not drawn.
//
// A row of a push class behind a feature flag (`feature` of BUZZ_EVENTS,
// `PUSH_CLASS_FEATURES`) stands while the world latches its flag: the
// PIRATES row of the NPC side under `pirates` (R2637), and the SEA MINES
// row of the Sapper under `sapper` (R2653). The game menu hands
// the card `state.hasFeatureFlag`. The hub knows no world, so it hands
// `envFlagRead` of the account: the flag set of the environment that
// `GET /api/me` carries (`features`, v2-the-deep.md §17.2, R2656). With no
// set the hub shows no such row. A class that is on stays on where its row
// is hidden, because the switches keep every class of PUSH_CLASSES.
//
// A push class that no buzz row names has a row of its own after the buzz
// rows: the CUP class of the clan cup, which the meta server sends itself
// and nothing in the game raises (R1629). Its BUZZ cell reads — on every
// telephone, and it has no pattern and no TRY.
//
// The card draws; `push/subscribe.ts` `PushAlerts` does the work. The
// switches take a press only while the controller is `ready`: on iOS
// outside the home-screen web app the card shows that step instead, and a
// browser with no web push, a server with no push and a refused
// permission each show one line and keep the switches disabled.
//
// The switches are account settings in the sense of §3.1, but they are
// NOT keys of the settings blob: the meta server acts on them, so they
// travel on their own route (`/api/push`), like the standing-orders
// switch of the PLAY section. A subscription belongs to one browser, so the
// head of the table and both rows under it carry the DEVICE mark (card 15).
//
// **Two hosts mount the card** (R1731): the hub SETTINGS panel and the game
// menu, which hands it a `PanelContext` of its own. The markup is the body
// of the section, with no page title, and it draws with the kit of
// `ui/options/` and the classes of `ui/options/cards.css` alone, so it reads
// the same under `#hub` and under `#ui`. The card reads nothing of the hub
// shell but the context.

import { oneOf } from '../../../util/oneof';
import { PUSH_CLASS_FEATURES, PUSH_CLASSES, type PushClass } from '../../../gen/balance';
import type { FeatureFlag } from '../../../gen/featureflags';
import {
  BUZZ_EVENTS, BUZZ_GAP_MS, BUZZ_HIT_GAP_MS, BUZZ_PATTERN_H, BUZZ_PATTERN_SPAN_MS, BUZZ_PATTERN_W,
  type BuzzEventId,
} from '../../../gen/presentation';
import { hubAlerts as S } from '../../../gen/strings';
import { browserEnv, PushAlerts, type PushEnv } from '../../../push/subscribe';
import { browserBuzzEnv, buzzForm, buzzRow, type BuzzEnv } from '../../buzz';
import { loadSettings, saveSettings } from '../../settings';
import { esc } from '../../esc';
import { groupHead, note, row, scopeMark, switchCtl } from '../../options/kit';
import { wireOnce, type PanelContext } from '../panel';
import { zoneLabel } from '../hubstate';
import { errNote } from './optcards';

/** The name of one push class: the name of the CUP row, and of a class a
 *  newer table adds before its words do. */
const CLASS_WORDS: Record<PushClass, string> = {
  siege: S.class_siege,
  hold: S.class_hold,
  fleet: S.class_fleet,
  economy: S.class_economy,
  cup: S.class_cup,
  pirates: S.class_pirates,
  sea_mines: S.class_sea_mines,
};

/** The name and the meaning of each push class that no buzz row names. */
const PUSH_ONLY_WORDS: Partial<Record<PushClass, [name: string, desc: string]>> = {
  cup: [S.ev_cup, S.class_cup_desc],
};

/** The push classes that no buzz row names, in the order of PUSH_CLASSES. */
export function pushOnlyClasses(hasFlag: FlagRead = () => false): PushClass[] {
  return shownClasses(hasFlag).filter((c) => !BUZZ_EVENTS.some((e) => e.push === c));
}

/** The name of each buzz event, and what it means, on a hover. */
const EVENT_WORDS: Record<BuzzEventId, [name: string, desc: string]> = {
  siege: [S.ev_siege, S.class_siege_desc],
  hold: [S.ev_hold, S.class_hold_desc],
  hit: [S.ev_hit, S.ev_hit_desc({ sec: BUZZ_HIT_GAP_MS / 1000 })],
  lost: [S.ev_lost, S.class_fleet_desc],
  economy: [S.ev_economy, S.class_economy_desc],
  pirates: [S.ev_pirates, S.class_pirates_desc],
  sea_mines: [S.ev_sea_mines, S.class_sea_mines_desc],
};

/** Whether the row of a flag shows: no flag, or a flag the world latches. */
export type FlagRead = (flag: FeatureFlag) => boolean;

/** The flag reader of the hub (R2656): the flag set of the environment
 *  that `GET /api/me` carries. A meta server that sends no set holds no
 *  flag, so the hub then shows no flagged row, as before R2656. */
export function envFlagRead(me: () => { features?: readonly string[] }): FlagRead {
  return (flag) => me().features?.includes(flag) ?? false;
}

/** A row or a class with no flag, or one whose flag holds (R2637). */
function shown(feature: string | undefined, hasFlag: FlagRead): boolean {
  return !feature || hasFlag(feature as FeatureFlag);
}

/** The push classes whose switch the card shows, in the order of
 *  PUSH_CLASSES. */
export function shownClasses(hasFlag: FlagRead): PushClass[] {
  return PUSH_CLASSES.filter((c) => shown(PUSH_CLASS_FEATURES[c], hasFlag));
}

/** The push class of a buzz row, or null for the hit. */
function pushOf(id: BuzzEventId): PushClass | null {
  const p = buzzRow(id).push;
  return oneOf(PUSH_CLASSES, p) ?? null;
}

/** Where the BUZZ switches live: the settings blob (R1613). The SETTINGS
 *  panel hands in its own copy, so one blob holds every switch of it. */
export interface BuzzStore {
  /** The BUZZ switches that are off. */
  off(): readonly BuzzEventId[];
  /** Stores the BUZZ switches that are off. */
  setOff(off: BuzzEventId[]): void;
}

/** The store of a card with no panel around it: the browser's blob. */
function blobStore(): BuzzStore {
  return {
    off: () => loadSettings().buzzOff,
    setOff: (off) => saveSettings({ ...loadSettings(), buzzOff: off }),
  };
}

/**
 * One pattern drawn to its length (card 11): a bar for each ON of the
 * pattern, against `BUZZ_PATTERN_SPAN_MS`, on a faint baseline.
 */
function patternSvg(pattern: readonly number[]): string {
  const w = BUZZ_PATTERN_W, h = BUZZ_PATTERN_H, k = w / BUZZ_PATTERN_SPAN_MS;
  let x = 0;
  let out = `<svg class="vpat" width="${w}" height="${h}" aria-hidden="true">`
    + `<line x1="0" y1="${h - 1}" x2="${w}" y2="${h - 1}"/>`;
  pattern.forEach((ms, i) => {
    if (i % 2 === 0) {
      out += `<rect x="${(x * k).toFixed(1)}" y="3" width="${Math.max(2, ms * k).toFixed(1)}" height="${h - 5}" rx="1.5"/>`;
    }
    x += ms;
  });
  return `${out}</svg>`;
}

/** The Share icon of Safari: a square open at the top with an arrow up
 *  out of it. The iOS step names it, so the reader finds the button. */
const SHARE_ICON = '<svg class="share" viewBox="0 0 16 16" aria-hidden="true" fill="none" '
  + 'stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">'
  + '<path d="M8 1.5v8.5M5 4.5l3-3 3 3M5.5 7H3.5v7.5h9V7h-2"/></svg>';

/** The part of a panel the SETTINGS panel mounts. */
export interface AlertsCard {
  /** Reads the subscription again. The panel calls it on each open. */
  open(): void;
  html(): string;
  /** Takes one press. Answers false for an action that is not the card's. */
  act(action: string, el: HTMLElement): boolean;
  wire(root: HTMLElement): void;
}

/** `heading` writes the group head as an `h3`, the level of the hub
 *  outline (R2048); the game menu (`options/gamehost.ts`) keeps the `div`.
 *  `hasFlag` reads the latched flags of the world: the game menu passes
 *  `state.hasFeatureFlag`, and the hub, which knows no world, the flag set
 *  of the environment (`envFlagRead`, R2656). */
export function createAlertsCard(
  ctx: PanelContext, env: PushEnv = browserEnv(),
  buzz: BuzzEnv = browserBuzzEnv(), store: BuzzStore = blobStore(), heading = false,
  hasFlag: FlagRead = () => false,
): AlertsCard {
  const ctl = new PushAlerts(env, () => ctx.refresh());
  /** What the device can do with the BUZZ column (R1615). */
  const form = buzzForm(buzz);

  /** One PUSH switch, or the night switch. It takes a press only while the
   *  controller is ready. `key` is the class. While push cannot work here
   *  at all (no web push, no key on the host, the home-screen step of iOS,
   *  a refused permission) the switch draws the unavailable face, and the
   *  blocker line says why (R1762); while the load runs or a call is in
   *  flight it is only paused. */
  function sw(on: boolean, act: string, label: string, key?: string): string {
    const unavailable = ctl.denied || (ctl.status !== 'ready' && ctl.status !== 'checking');
    return switchCtl(on, { act, key }, { label, disabled: !ctl.enabled, unavailable });
  }

  /** The two clock boxes of the quiet hours, each in a box with its word.
   *  The value rides in the markup and `wire` puts it on the element too. */
  function hours(): string {
    const off = ctl.status === 'ready' && !ctl.busy ? '' : ' disabled';
    const box = (name: string, word: string, value: string) => `<label class="c-time"><i>${esc(word)}</i>`
      + `<input type="time" data-in="${name}" aria-label="${esc(word)}" value="${esc(value)}"${off}></label>`;
    return '<span class="c-hours">'
      + box('quiet-start', S.quiet_from, ctl.prefs.quietStart)
      + box('quiet-end', S.quiet_to, ctl.prefs.quietEnd) + '</span>';
  }

  /** The line that says why the switches do not work, or ''. */
  function blocker(): string {
    switch (ctl.status) {
      case 'ios-install':
        return `<div class="o-call c-ios">${S.ios_step({ share: SHARE_ICON })}</div>`;
      case 'unsupported':
        return note(S.unsupported, true);
      case 'unavailable':
        return note(S.unavailable, true);
      default:
        return ctl.denied ? note(ctl.native ? S.refused_app : S.refused, true) : '';
    }
  }

  /** The event cell: the name, and under it the sentence on a desktop, or
   *  the pattern and ▶ TRY on a telephone. */
  function eventCell(name: string, desc: string, pattern: readonly number[] | null, id: string): string {
    const motor = form === 'motor';
    let under = '';
    if (form === 'desk') under = desc === '' ? '' : `<i>${esc(desc)}</i>`;
    else if (pattern !== null) {
      under = `<span class="c-pat">${patternSvg(pattern)}${motor
        ? `<button type="button" class="c-try" data-act="buzz-try" data-key="${esc(id)}"`
          + ` title="${esc(S.buzz_try_title)}" aria-label="${esc(S.buzz_try_title)}">${esc(S.buzz_try)}</button>`
        : ''}</span>`;
    }
    return `<span class="c-ev"><b>${esc(name)}</b>${under}</span>`;
  }

  /**
   * The table of card 16: one line per buzz event, with a PUSH switch and,
   * on a telephone, a BUZZ switch beside it. A desktop draws the PUSH
   * column alone, and no line for the hit, which has no push.
   */
  function table(): string {
    const motor = form === 'motor';
    const desk = form === 'desk';
    const cols = desk ? ' c1' : ' c2';
    const off = store.off();
    const na = `<span class="c-none">${esc(S.none)}</span>`;
    const head = `<div class="c-mxr c-mxh${cols}"><span>${esc(S.col_event)}</span><span>${esc(S.col_push)}</span>`
      + (desk ? '' : `<span>${esc(S.col_buzz)}</span>`) + '</div>';
    const lines = BUZZ_EVENTS.map((ev) => {
      const push = pushOf(ev.id);
      if (desk && push === null) return '';
      if (!shown(ev.feature, hasFlag)) return '';
      const [name, desc] = EVENT_WORDS[ev.id];
      const pushCell = push === null ? na : sw(ctl.prefs.classes.includes(push), 'push-class', name, push);
      const buzzCell = desk ? '' : `<span>${motor
        ? switchCtl(!off.includes(ev.id), { act: 'buzz', key: ev.id }, { label: `${S.col_buzz} · ${name}` })
        : na}</span>`;
      return `<div class="c-mxr${cols}" title="${esc(desc)}">${eventCell(name, desc, ev.pattern, ev.id)}`
        + `<span>${pushCell}</span>${buzzCell}</div>`;
    }).join('') + pushOnlyClasses(hasFlag).map((c) => {
      const [name, desc] = PUSH_ONLY_WORDS[c] ?? [CLASS_WORDS[c], ''];
      return `<div class="c-mxr${cols}" title="${esc(desc)}">${eventCell(name, desc, null, c)}`
        + `<span>${sw(ctl.prefs.classes.includes(c), 'push-class', name, c)}</span>`
        + (desk ? '' : `<span>${na}</span>`) + '</div>';
    }).join('');
    return `<div class="c-mx">${head}${lines}</div>`;
  }

  return {
    open(): void {
      if (!ctl.busy) void ctl.load();
    },

    html(): string {
      const desk = form === 'desk';
      return note(S.lede)
        + (form === 'motor' ? note(S.lede_buzz) : form === 'none' ? note(S.lede_no_buzz) : '')
        + blocker()
        + groupHead(desk ? S.head_push : S.head_push_buzz,
          ctl.status === 'checking' ? S.checking : '', ` ${scopeMark('device')}`, heading)
        + table()
        + (desk ? '' : note(S.buzz_foot({ sec: BUZZ_GAP_MS / 1000 })))
        + row({
          name: S.quiet, what: S.quiet_desc({ zone: zoneLabel(env.timeZone(), ctx.now()) }), scope: 'device',
          control: hours(), wrap: !ctx.wide(),
        })
        + row({
          name: S.night, what: S.night_desc, scope: 'device',
          control: sw(ctl.prefs.siegeAtNight, 'push-night', S.night),
        })
        + errNote(ctl.err)
        + note(ctl.native ? S.privacy_app : S.privacy);
    },

    act(action: string, el: HTMLElement): boolean {
      if (action === 'push-class') {
        const c = oneOf(PUSH_CLASSES, el.dataset.key);
        if (!c || !ctl.enabled || !shown(PUSH_CLASS_FEATURES[c], hasFlag)) return true;
        ctx.play('sfx-menu-toggle');
        ctl.setClass(c, !ctl.prefs.classes.includes(c));
        return true;
      }
      if (action === 'buzz') {
        const ev = BUZZ_EVENTS.find((e) => e.id === el.dataset.key);
        if (!ev || form !== 'motor' || !shown(ev.feature, hasFlag)) return true;
        ctx.play('sfx-menu-toggle');
        const off = store.off().filter((id) => id !== ev.id);
        if (!store.off().includes(ev.id)) off.push(ev.id);
        store.setOff(off);
        ctx.refresh();
        return true;
      }
      if (action === 'buzz-try') {
        const ev = BUZZ_EVENTS.find((e) => e.id === el.dataset.key);
        if (ev && form === 'motor') buzz.vibrate?.([...ev.pattern]);
        return true;
      }
      if (action === 'push-night') {
        if (!ctl.enabled) return true;
        ctx.play('sfx-menu-toggle');
        ctl.setNight(!ctl.prefs.siegeAtNight);
        return true;
      }
      return false;
    },

    wire(root: HTMLElement): void {
      const start = root.querySelector<HTMLInputElement>('input[data-in="quiet-start"]');
      const end = root.querySelector<HTMLInputElement>('input[data-in="quiet-end"]');
      if (!start || !end) return;
      start.value = ctl.prefs.quietStart;
      end.value = ctl.prefs.quietEnd;
      for (const box of [start, end]) {
        // `change` fires when the picker closes, not on every tick of the
        // wheel, so one pick is one write.
        if (wireOnce(box)) box.addEventListener('change', () => { ctl.setQuiet(start.value, end.value); });
      }
    },
  };
}
